import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIntegrationName } from "./useIntegrationName";
import { CapsuleButton, CustomNumberInput } from "../../../Everou/_components";
import { useDispatch } from "react-redux";
import { MIN_TIME } from "../../../Everou/_constants";
import { useError } from "../../../Everou/_hooks/useError";
import { Icons } from "../../../Everou/_assets";
import SendInvitationURLToggle from "../../../Everou/IntegrationsModule/components/SendInvitationURLToggle";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { CheckInOutFields } from "../../../Everou/IntegrationsModule/components/CheckInOutFields";
import { IntegrationSectionTitle } from "../../../Everou/IntegrationsModule/components/IntegrationSectionTitle";

type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onSetInitialStatus: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
}

export function PMSKrossBookingIntegrationInput({ 
    integration, 
    onDelete, 
    index, 
    onToggleEditable }: IntegrationInputProps) {

    // LIBRARIES
    const { t } = useTranslation();
    const { templateName, nameInput } = useIntegrationName(integration);
    const dispatch = useDispatch();
    
    // GLOBAL STATE
    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [hotelIDValue, setHotelIDValue] = useState(integration.hotel_id ?? '');
    const [usernameValue, setUsernameValue] = useState(integration.username ?? '');
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL);
    const [daysForInvitationCreate, setDaysForInvitationCreate] = useState(integration.daysForInvitationCreate ?? 1);
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);


    const updateDaysForInvitationCreation = (daysForInvitationCreate : number) => {
        setDaysForInvitationCreate(daysForInvitationCreate);
    };

    // INITIALIZATION
    useEffect(() => { 
        setApikeyValue(integration.apikey ?? '');
        setHotelIDValue(integration.hotel_id ?? '');
        setUsernameValue(integration.username ?? '');
        setSendInvitationURL(integration.sendInvitationURL);
        setCheckIn(integration.check_in ?? '00:00');
        setCheckOut(integration.check_out ?? '00:00');
        setDaysForInvitationCreate(integration.daysForInvitationCreate ?? 1);
    }, [integration]);

    const canSave = useMemo(() => {
        if(hasError) return false;

        return !!apikeyValue && !!hotelIDValue && !!usernameValue && 
        (
            !integration.uid ||
            sendInvitationURL !== integration.sendInvitationURL || 
            integration.createPublicUrl !== true ||
            apikeyValue !== integration.apikey ||
            hotelIDValue !== integration.hotel_id ||
            usernameValue !== integration.username ||
            checkIn !== integration.check_in ||
            checkOut !== integration.check_out ||
            daysForInvitationCreate !== integration.daysForInvitationCreate
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        integration.uid,
        sendInvitationURL, 
        apikeyValue, 
        hotelIDValue, 
        usernameValue, 
        hasError,
        checkIn,
        checkOut,
        daysForInvitationCreate
    ]);

    const onSave = useCallback(async () => {
        const editedIntegration: Integration = {
            name: templateName,
            apikey: apikeyValue,
            hotel_id: hotelIDValue,
            username: usernameValue,
            company: integration.company,
            uid: integration.uid,
            check_in: checkIn,
            check_out: checkOut,
            // Not user-configurable: without the public link the guest has to accept
            // the invitation from the app, and every account that matters runs with it
            // on. Same policy as Hostify/Avaibook.
            createPublicUrl: true,
            sendInvitationURL,
            daysForInvitationCreate
        }

        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
        } catch (err: any) {
            setError(err.message)
        }
    }, [
        dispatch,
        templateName, 
        t, 
        setError, 
        integration.company,
        integration.uid, 
        apikeyValue, 
        hotelIDValue, 
        usernameValue,
        sendInvitationURL,
        checkIn,
        checkOut,
        daysForInvitationCreate
    ]);

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            <IntegrationSectionTitle title={t("mod_integrations_section_credentials")} />
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">API Key</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={apikeyValue}
                    placeholder={"API Key"}
                    onChange={(e) => {
                        setApikeyValue(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">Hotel ID</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={hotelIDValue}
                    placeholder={"Hotel ID"}
                    onChange={(e) => {
                        setHotelIDValue(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">User</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={usernameValue}
                    placeholder={"User"}
                    onChange={(e) => {
                        setUsernameValue(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div className="mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_delivery")} />
            </div>
            <div>
                <div>
                    <SendInvitationURLToggle
                        optional
                        boldedName={false}
                        initialState={sendInvitationURL}
                        readOnly={!integration.editable}
                        onChange={setSendInvitationURL}
                    />
                </div>
                <div className="d-flex align-items-start c-bg-grey-250 c-rounded-big p-2 mt-2">
                    <img
                        src={Icons.info}
                        alt="info"
                        className="mr-2 flex-shrink-0"
                        style={{ width: "18px", height: "18px" }}
                    />
                    <span className="small text-muted">{t("mod_integrations_kross_custom_field_info")}</span>
                </div>
            </div>
            <div className="mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_schedule")} />
            </div>
            <div>
            <CheckInOutFields
                checkIn={checkIn}
                checkOut={checkOut}
                onChangeCheckIn={setCheckIn}
                onChangeCheckOut={setCheckOut}
                readOnly={!integration.editable}
            />
            </div>
            <div className="mt-2 d-flex align-items-center justify-content-between">
                <ToolTipWrapper tooltip={t("mod_integrations_days_for_invitation_create_tooltip")}>
                    <label className="font-weight-bold mb-0 mr-2">{t("mod_integrations_days_for_invitation_create")}</label>
                </ToolTipWrapper>
                <CustomNumberInput
                    value={daysForInvitationCreate}
                    min={1}
                    max={5}
                    readOnly={!integration.editable}
                    onChange={updateDaysForInvitationCreation}
                />
            </div>
            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? 'PRIMARY' : 'INFO'}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                        />
                    }
                    {integration.uid &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                </div>
                <CapsuleButton
                    size="SMALL"
                    isEnabled={canSave}
                    onClick={onSave}
                    text={t("global_save")}
                    />

            </div>
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}